import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import type { OrganizationRole, OrganizationSummary } from '@core/types/api';
import {
  selectCurrentUser,
  selectIsAuthenticated,
  selectOrganizations,
} from '@features/auth/store/authSlice';
import { useListOrganizationsQuery } from './api/organizationApi';
import {
  preferredOrganizationUuid,
  selectActiveOrganizationUuid,
  selectRejectedOrganizationUuids,
  setActiveOrganization,
} from './store/organizationSlice';

export interface UseActiveOrganizationResult {
  /** The active organization uuid (falls back to the preferred one when unset). */
  uuid: string | null;
  /** The active organization, once it resolves against the loaded list. */
  organization: OrganizationSummary | undefined;
  /** Every organization the caller may act on. */
  organizations: OrganizationSummary[];
  /** The caller's role in the active organization (null for a pure superuser). */
  role: OrganizationRole | null;
  /** Whether the caller may administer the active organization. */
  isAdmin: boolean;
  /** Whether there is more than one organization to switch between. */
  canSwitch: boolean;
  /** Whether the organizations query is still in flight. */
  isLoading: boolean;
  /** No organization could be selected even though the caller has some (all 403'd). */
  hasNoSelectableOrganization: boolean;
}

/**
 * Resolve the active organization (the tenant every request is scoped to).
 *
 * The login response already carries the caller's organizations, so the header
 * renders instantly from the session while `listOrganizations` refreshes names
 * and roles in the background. When nothing is persisted yet the preferred
 * organization is selected and stored, so the next request carries the right
 * `X-Organization` header — and it survives a reload.
 */
export function useActiveOrganization(): UseActiveOrganizationResult {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const activeUuid = useAppSelector(selectActiveOrganizationUuid);
  const rejectedUuids = useAppSelector(selectRejectedOrganizationUuids);
  const sessionOrganizations = useAppSelector(selectOrganizations);
  const { data, isLoading } = useListOrganizationsQuery(undefined, { skip: !isAuthenticated });

  const organizations = data ?? sessionOrganizations;
  // Rejected uuids are skipped: `organizations` falls back to the persisted login
  // payload, which still lists the organization the backend just 403'd, so without
  // this the effect below would re-select it and the request would 403 again.
  const preferred = preferredOrganizationUuid(
    organizations,
    user?.default_organization_uuid,
    rejectedUuids,
  );
  const uuid = activeUuid ?? preferred;

  // Persist the default selection so the header is set on the next request.
  useEffect(() => {
    if (!activeUuid && preferred) {
      dispatch(setActiveOrganization(preferred));
    }
  }, [activeUuid, preferred, dispatch]);

  const organization = uuid
    ? organizations.find((candidate) => candidate.uuid === uuid)
    : undefined;
  const role = organization?.role ?? null;

  return {
    uuid,
    organization,
    organizations,
    role,
    isAdmin: role === 'admin' || (user?.is_superuser ?? false),
    canSwitch: organizations.length > 1,
    isLoading,
    hasNoSelectableOrganization: uuid === null && organizations.length > 0,
  };
}
